import {
  Component,
  signal,
  computed,
  effect,
  inject,
  afterNextRender,
  Injector,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { PageTitleComponent } from '@awo-care/shared-ui';
import { getFaqCategories } from './faq.data';

@Component({
  selector: 'app-faq',
  imports: [
    MatExpansionModule,
    MatIconModule,
    RouterLink,
    PageTitleComponent,
  ],
  template: `
    <app-page-title
      title="Häufige Fragen"
      [centered]="true"
      i18n-title="@@faq.title"
    />

    <div class="faq-container">
      <p class="faq-subtitle" i18n="@@faq.subtitle">
        Hier finden Sie Antworten auf die häufigsten Fragen zur Nutzung von AWO
        Care.
      </p>

      <div class="category-filter">
        <button
          class="filter-chip"
          [class.active]="selectedCategory() === null"
          (click)="selectedCategory.set(null)"
          i18n="@@faq.filter.all"
        >
          Alle
        </button>
        @for (category of categories; track category.id) {
          <button
            class="filter-chip"
            [class.active]="selectedCategory() === category.id"
            (click)="selectedCategory.set(category.id)"
          >
            <mat-icon aria-hidden="true">{{ category.icon }}</mat-icon>
            {{ category.title }}
          </button>
        }
      </div>

      @for (category of filteredCategories(); track category.id) {
        <div class="category-section">
          <h2 class="category-title">
            <mat-icon aria-hidden="true">{{ category.icon }}</mat-icon>
            {{ category.title }}
          </h2>

          <mat-accordion multi>
            @for (item of category.items; track item.id) {
              <mat-expansion-panel
                [id]="'faq-' + item.id"
                [expanded]="item.id === highlightedId()"
                [class.highlighted]="item.id === highlightedId()"
              >
                <mat-expansion-panel-header>
                  <mat-panel-title>{{ item.question }}</mat-panel-title>
                </mat-expansion-panel-header>
                <p class="faq-answer">{{ item.answer }}</p>
              </mat-expansion-panel>
            }
          </mat-accordion>
        </div>
      }

      <div class="contact-hint">
        <mat-icon aria-hidden="true">help_outline</mat-icon>
        <p i18n="@@faq.contactHint">
          Ihre Frage ist nicht dabei?
          <a routerLink="/contact">Kontaktieren Sie uns</a>
          — wir helfen Ihnen gerne weiter.
        </p>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 3rem 2rem;
      }

      .faq-container {
        max-width: 1000px;
        width: 100%;
      }

      .faq-subtitle {
        text-align: center;
        color: var(--awo-text-muted, #6f6f6f);
        font-size: 1.1rem;
        margin-bottom: 2rem;
        line-height: 1.5;
      }

      .category-filter {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        justify-content: center;
        margin-bottom: 2rem;
      }

      .filter-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
        padding: 0.4rem 0.9rem;
        border-radius: 20px;
        border: 1px solid var(--theme-border);
        background: white;
        color: var(--theme-regularTextColor);
        font-size: 0.85rem;
        cursor: pointer;
        transition: all 0.2s;
        font-family: inherit;
      }

      .filter-chip mat-icon {
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      .filter-chip:hover {
        border-color: var(--theme-primary);
        color: var(--theme-primary);
      }

      .filter-chip.active {
        background: var(--theme-primary);
        border-color: var(--theme-primary);
        color: white;
      }

      .category-section {
        margin-bottom: 2rem;
      }

      .category-title {
        font-size: 1.15rem;
        font-weight: 500;
        color: var(--theme-regularTextColor);
        margin-bottom: 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      .category-title mat-icon {
        color: var(--theme-primary);
        font-size: 22px;
        width: 22px;
        height: 22px;
      }

      .faq-answer {
        line-height: 1.7;
        color: #555;
        margin: 0;
      }

      /* Die per /faq#<id> verlinkte Frage — sichtbar abgesetzt, damit auf der
         langen Seite klar ist, worauf verwiesen wurde. */
      mat-expansion-panel.highlighted {
        border-left: 4px solid var(--theme-primary);
        background: var(--theme-info-surface);
      }

      /* Abstand fuer die feste Kopfleiste, damit die angesprungene Frage nicht
         darunter verschwindet. */
      mat-expansion-panel {
        scroll-margin-top: 96px;
      }

      .contact-hint {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-top: 2rem;
        padding: 1.25rem 1.5rem;
        background: var(--theme-info-surface);
        border-radius: 8px;
        border: 1px solid var(--theme-info-border);
      }

      .contact-hint mat-icon {
        color: var(--theme-accent-text, #08acd3);
        flex-shrink: 0;
      }

      .contact-hint p {
        margin: 0;
        font-size: 0.9rem;
        line-height: 1.5;
        color: var(--theme-regularTextColor);
      }

      .contact-hint a {
        color: var(--theme-accent-text, #08acd3);
        font-weight: 500;
        text-decoration: underline;
      }

      @media (max-width: 768px) {
        :host {
          padding: 2rem 1rem;
        }

      }
    `,
  ],
})
export class FaqComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly injector = inject(Injector);

  readonly categories = getFaqCategories();
  readonly selectedCategory = signal<string | null>(null);

  /**
   * Die per `/faq#<id>` verlinkte Frage. Sie wird aufgeklappt, hervorgehoben
   * und in den Sichtbereich gescrollt — auf einer Seite mit über 25 Fragen ist
   * sonst nicht erkennbar, worauf verwiesen wurde.
   */
  readonly highlightedId = toSignal(this.route.fragment, {
    initialValue: null,
  });

  readonly filteredCategories = computed(() => {
    const selected = this.selectedCategory();
    if (!selected) return this.categories;
    return this.categories.filter((c) => c.id === selected);
  });

  constructor() {
    effect(() => {
      const id = this.highlightedId();
      // Die Seite wird prerendert (SSG): dort gibt es kein Fragment und kein
      // DOM zum Scrollen.
      if (!id || !isPlatformBrowser(this.platformId)) {
        return;
      }
      // Ein verlinkter Anker soll auch dann gefunden werden, wenn zuvor eine
      // Kategorie gefiltert war.
      this.selectedCategory.set(null);
      afterNextRender(
        () => {
          // Erst nach dem Aufklappen scrollen: die Expansion-Animation
          // verschiebt das Layout, ein sofortiger Scroll landet daneben.
          this.document.defaultView?.setTimeout(() => {
            this.document
              .getElementById(`faq-${id}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 350);
        },
        { injector: this.injector }
      );
    });
  }
}
